import React, { useState } from 'react';
import { ChevronRight, Layers } from 'lucide-react';

/**
 * ClusterList — List of issue/suggestion clusters.
 *
 * Props:
 *   clusters: Array<{ label, item_count, severity/demand, feature_area, items? }>
 *   title: string
 *   emptyMessage: string
 *   onSelect: (cluster) => void
 *   accentColor: string
 */

const ClusterList = ({
    clusters = [], title = 'Clusters',
    emptyMessage = 'No clusters found',
    onSelect = null, accentColor = '#6366f1',
}) => {
    if (clusters.length === 0) {
        return (
            <div style={{
                textAlign: 'center', padding: '24px 0',
                color: '#94a3b8', fontSize: 13,
            }}>
                <Layers size={24} style={{ margin: '0 auto 6px', opacity: 0.4 }} />
                <p style={{ margin: 0 }}>{emptyMessage}</p>
            </div>
        );
    }

    return (
        <div style={{
            background: '#fff', border: '1px solid #e5e7eb',
            borderRadius: 12, overflow: 'hidden',
        }}>
            {clusters.map((c, i) => (
                <div
                    key={i}
                    onClick={() => onSelect && onSelect(c)}
                    style={{
                        padding: '14px 16px',
                        borderBottom: i < clusters.length - 1 ? '1px solid #f1f5f9' : 'none',
                        cursor: onSelect ? 'pointer' : 'default',
                        transition: 'background 0.1s',
                        display: 'flex', alignItems: 'center', gap: 12,
                    }}
                    onMouseOver={e => { if (onSelect) e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseOut={e => { if (onSelect) e.currentTarget.style.background = 'transparent'; }}
                >
                    {/* Severity/demand indicator */}
                    <div style={{
                        width: 6, height: 32, borderRadius: 3,
                        background: accentColor,
                        opacity: Math.max(0.3, Math.min(1, (c.item_count || 1) / 10)),
                        flexShrink: 0,
                    }} />

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                            {c.label || c.feature_area || 'Unknown'}
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                            {c.feature_area && c.feature_area !== c.label && (
                                <span style={{
                                    fontSize: 10, fontWeight: 500,
                                    background: '#f1f5f9', color: '#64748b',
                                    padding: '1px 6px', borderRadius: 4,
                                }}>
                                    {c.feature_area}
                                </span>
                            )}
                            <span style={{ fontSize: 11, color: '#6b7280' }}>
                                {c.item_count || 0} item{(c.item_count || 0) !== 1 ? 's' : ''}
                            </span>
                        </div>
                    </div>

                    {/* Arrow */}
                    {onSelect && (
                        <ChevronRight size={14} style={{ color: '#94a3b8' }} />
                    )}
                </div>
            ))}
        </div>
    );
};

export default ClusterList;
